import React from 'react';
import './ViewMessage.css';
import IconSVG from "../IconSVG";

export type ViewMessageType = 'success' | 'error' | 'warning';

export interface ViewMessageProps {
  show: boolean;
  // New API: separate title and text
  title?: string;
  text?: string;
  /**
   * Deprecated: use title/text instead. If provided and title/text are absent,
   * it will be rendered as the text.
   */
  message?: string;
  type?: ViewMessageType; // default to 'success'
  className?: string;
  // aria-live for accessibility, defaults based on type
  ariaLive?: 'assertive' | 'polite' | 'off';
  // Optional close handler: when provided, a close button is rendered
  onClose?: () => void;
  closeAriaLabel?: string;
}

const typeToAriaLive: Record<ViewMessageType, 'assertive' | 'polite'> = {
  success: 'polite',
  warning: 'polite',
  error: 'assertive',
};

const ViewMessage: React.FC<ViewMessageProps> = ({
  show,
  title,
  text,
  message,
  type = 'success',
  className = '',
  ariaLive,
  onClose,
  closeAriaLabel,
}) => {
  if (!show) return null;

  const live = ariaLive ?? typeToAriaLive[type];

  const resolvedTitle = title;
  const resolvedText = text ?? (!title ? message : text ?? message);

  return (
    <div
      className={`view-message view-message--${type} ${className}`.trim()}
      role={type === 'error' ? 'alert' : 'status'}
      aria-live={live}
    >
      <span className="view-message__icon" aria-hidden>
        {type === 'success' && (
          // Circle check icon
          <IconSVG path={'/images/ui-svg/circle-alert-success.svg'} size={24}/>
        )}
        {type === 'warning' && (
          // Info/warning icon
          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
            <circle cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="2" fill="none" />
            <line x1="12" y1="8" x2="12" y2="12" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
            <circle cx="12" cy="16" r="1.25" fill="currentColor" />
          </svg>
        )}
        {type === 'error' && (
          // Error icon
          <IconSVG path={'/images/ui-svg/circle-alert-error.svg'} size={24}/>
        )}
      </span>
      <span className="view-message__content">
        {resolvedTitle && <span className="view-message__title">{resolvedTitle}</span>}
        {(resolvedText || (!resolvedTitle && !resolvedText)) && (
          <span className="view-message__text">{resolvedText}</span>
        )}
      </span>
      {onClose && (
        <button
          type="button"
          className="view-message__close"
          aria-label={closeAriaLabel || 'Close message'}
          onClick={onClose}
        >
          <IconSVG path={type === 'error' ? '/images/ui-svg/message-close-icon.svg' : '/images/ui-svg/message-close-icon-success.svg'} size={8}/>
        </button>
      )}
    </div>
  );
};

export default ViewMessage;
